import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { delay } from "go-core";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { Loading, LoadingContainer } from "go-core/components/Loading";
import { ReactComponent as DangerAlertSVG } from "go-core/images/svg/alert.svg";
import { ReactComponent as FileSVG } from "go-core/images/svg/file.svg";
import { ReactComponent as WarningAlertSVG } from "go-core/images/svg/warning-alert.svg";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { organizationApi } from "../../../../../../../../services/Api/Organization/organizationApi";
import { PaymentSourceApi } from "../../../../../../../../services/Api/Organization/organizationTypes";
import { SubscriptionInvoiceApi } from "../../../../../../../../services/Api/Subscription/subscriptionTypes";
import { selectOrganization } from "../../../../../../../../services/organizations/selectors";
import { hasRole } from "../../../../../../../../services/users/selectors";
import { getApiURL } from "../../../../../../../../utils/envutil";
import { getCreditCardExpirationDays } from "../../services/getCreditCardExpirationDays";
import PaymentModal from "../Payment/PaymentModal";

interface Props {
	invoices: SubscriptionInvoiceApi[];
}

const SubscriptionsPayment: FC<Props> = ({ invoices }) => {
	const organization = useSelector(selectOrganization);
	const [paymentSource, setPaymentSource] = useState<PaymentSourceApi>();
	const [loading, setLoading] = useState<boolean>(true);
	const [loadingCard, setLoadingCard] = useState<boolean>(false);
	const [pendingCard, setPendingCard] = useState<boolean>(false);
	const [activatingCard, setActivatingCard] = useState<boolean>(false);
	const mountedRef = useRef(true);
	const initialRender = useRef(true);
	const { addFlash } = useFlash();
	const { t } = useTranslation();
	const confirmation = useConfirmation();
	const cardChecking = useRef(false);
	const hasOfficeRole = useSelector(hasRole("OFFICE"));
	const [showPaymentModal, setShowPaymentModal] = useState(false);

	useEffect(() => {
		const query = new URLSearchParams(window.location.search);
		if (query.get("ServiceID")) {
			setPendingCard(true);
			checkPaymentStatus();
		} else {
			fetchCard();
		}
	}, []);

	const checkPaymentStatus = useCallback(async () => {
		setLoading(false);
		try {
			while (mountedRef.current) {
				let res: any;
				if (!hasOfficeRole) {
					if (initialRender.current) {
						res = await organizationApi.getBillingCard();
						initialRender.current = false;
					} else {
						await delay(2000);
						res = await organizationApi.getBillingCard();
					}
				}
				if (res.data.data) {
					mountedRef.current = false;
					setPaymentSource(res.data.data);
					setPendingCard(false);
				}
			}
		} catch (err) {
			mountedRef.current = false;
			setPendingCard(false);
			handleError.alert(err, addFlash);
		}
	}, []);

	const fetchCard = async () => {
		setLoading(true);
		try {
			if (!hasOfficeRole) {
				const res = await organizationApi.getBillingCard();
				setPaymentSource(res);
				if (["VALID", "INVALID", "DISABLED"].includes(res?.status)) {
					cardChecking.current = false;
					setLoadingCard(false);
					setPendingCard(false);
				}
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoading(false);
	};

	const removeCard = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.remove", { ns: "lib" }),
			});
			setLoadingCard(true);
			await organizationApi.deleteBillingCard();
			await fetchCard();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoadingCard(false);
	};

	const intervalCallback = () => {
		if (cardChecking.current) {
			fetchCard();
		}
	};

	useEffect(() => {
		const interval = setInterval(() => intervalCallback(), 3000);
		return () => clearInterval(interval);
	}, []);

	const addCard = async () => {
		setShowPaymentModal(true);
		setLoadingCard(true);
	};

	const activateCard = async () => {
		setActivatingCard(true);
		await organizationApi.activateBillingCard();
		await fetchCard();
		setActivatingCard(false);
	};

	const renderStatus = (status: string) => {
		let statusMessage = "";
		let statusColor = "warning";

		if (
			paymentSource?.card &&
			getCreditCardExpirationDays(paymentSource.card.expired_at) > 0 &&
			getCreditCardExpirationDays(paymentSource.card.expired_at) <= 30 &&
			paymentSource.status !== "INVALID" &&
			paymentSource.status !== "EXPIRED"
		) {
			return (
				<div className="d-flex">
					{getCreditCardExpirationDays(paymentSource.card.expired_at, organization.timezone) <= 14 ? (
						<DangerAlertSVG className="mt-1" />
					) : (
						<WarningAlertSVG className="mt-1" />
					)}
					<p
						className={`${
							getCreditCardExpirationDays(paymentSource.card.expired_at, organization.timezone) <= 14
								? "text-danger"
								: "text-warning"
						} font-weight-normal mb-0 ms-1`}
					>
						{t("modules.subscription.payment_cart_to_be_expired_in.title", {
							count: getCreditCardExpirationDays(paymentSource.card.expired_at, organization.timezone),
						})}
					</p>
				</div>
			);
		}
		switch (status) {
			case "EXPIRED":
				statusMessage = t("enums.payment_card.status.EXPIRED");
				statusColor = "danger";
				break;
			case "INVALID":
				statusMessage = t("enums.payment_card.status.INVALID");
				statusColor = "danger";
				break;
			case "PENDING_VERIFICATION":
				statusMessage = t("enums.payment_card.status.PENDING_VERIFICATION");
				break;
		}
		return <p className={`text-${statusColor} font-weight-normal mb-0`}>{statusMessage}</p>;
	};

	const renderValidCardExpirationDate = () => {
		if (paymentSource?.status === "VALID" || paymentSource?.status === "EXPIRING") {
			return (
				<div className="mb-1">
					<span className="text-muted">
						<span className="text-success me-2">{t("modules.subscription.field.active_female.title")}</span>
						{t("lib:common.word.to")}: {FormatDate(paymentSource.card.expired_at).split(",")[0]}
					</span>
				</div>
			);
		}
	};

	const getUnpaidInvoicesTotalMoney = () => {
		const unpaidInvoices = invoices.filter((invoice) => invoice.status !== "PAID");
		if (unpaidInvoices.length === 0) return 0;
		return Number(unpaidInvoices.reduce((acc, cur) => acc + (cur?.due_money?.amount || 0), 0));
	};

	if (hasOfficeRole) return <></>;

	return (
		<>
			<Card className="subscription-info-card">
				{loading ? (
					<LoadingContainer />
				) : (
					<>
						{pendingCard ? (
							<>
								<Card.Body>
									<div className="d-flex align-items-center">
										<div className="subscription-card-icon-box me-2">
											<FileSVG />
										</div>
										<h5 className="mb-0">{t("modules.subscription.field.payment.title")}</h5>
									</div>
									<div className="mt-3">
										<Loading
											title={t("modules.subscription.field.verification_in_progress.title")}
										/>
									</div>
									<div className={"card-body-footer"}>
										<ButtonLoading
											className={"p-0"}
											loading={loadingCard}
											variant="link"
											onClick={addCard}
										>
											{t("modules.subscription.action.add_card.title")}
										</ButtonLoading>
									</div>
								</Card.Body>
							</>
						) : (
							<>
								{paymentSource && paymentSource.status !== "INVALID" ? (
									<>
										<Card.Body key="card-with-payment-source">
											<div className="d-flex align-items-center">
												<div className="subscription-card-icon-box me-2">
													<FileSVG />
												</div>
												<h5 className="mb-0">
													{t("modules.subscription.field.payment.title")}
												</h5>
											</div>
											<div className="mt-3">
												<h5>
													{paymentSource.card?.issuer} {paymentSource.card?.mask_number} ****
													**** ****
												</h5>
												{renderValidCardExpirationDate()}
												{renderStatus(paymentSource.status)}

												{paymentSource.status !== "VALID" && paymentSource.error_message && (
													<p className={"text-danger font-weight-normal mb-0"}>
														{paymentSource.error_message}
													</p>
												)}
											</div>
											<div className={"card-body-footer"}>
												<ButtonLoading
													className={"p-0 me-1"}
													loading={loadingCard}
													variant="link"
													onClick={addCard}
												>
													{t("common.action.edit", { ns: "lib" })}
												</ButtonLoading>
												{paymentSource.status === "INVALID" && (
													<ButtonLoading
														className={"p-0 me-1"}
														loading={activatingCard}
														variant="link"
														onClick={activateCard}
													>
														{t("lib:common.action.activate")}
													</ButtonLoading>
												)}
												<ButtonLoading
													className="p-0 remove-button"
													loading={loadingCard}
													variant="link"
													onClick={removeCard}
												>
													{t("common.action.remove", { ns: "lib" })}
												</ButtonLoading>
											</div>
										</Card.Body>
									</>
								) : (
									<>
										<Card.Body key="card-without-payment-source">
											<div className="d-flex align-items-center">
												<div className="subscription-card-icon-box me-2">
													<FileSVG />
												</div>
												<h5 className="mb-0">
													{t("modules.subscription.field.payment.title")}
												</h5>
											</div>
											<div className="mt-3">
												<h5>
													{getUnpaidInvoicesTotalMoney() > 0
														? t("modules.subscription.field.plug_card.title")
														: t("modules.subscription.field.add_card.title")}
												</h5>
												{getUnpaidInvoicesTotalMoney() > 0 ? (
													<p className="text-danger font-weight-normal mb-0">
														{t("modules.subscription.field.to_pay.title", {
															amount: FormatMoney({
																amount: getUnpaidInvoicesTotalMoney(),
																currency: "PLN",
															}),
														})}
													</p>
												) : (
													<p className="font-weight-normal mb-0">
														{t("modules.subscription.field.subscription_auto.title")}
													</p>
												)}
											</div>
											<div className={"card-body-footer"}>
												<ButtonLoading
													className={"p-0"}
													loading={loadingCard}
													variant="link"
													onClick={addCard}
												>
													{t("modules.subscription.action.add_card.title")}
												</ButtonLoading>
											</div>
										</Card.Body>
									</>
								)}
							</>
						)}
					</>
				)}
			</Card>
			{showPaymentModal && (
				<PaymentModal
					show={showPaymentModal}
					onHide={() => {
						setShowPaymentModal(false);
						setLoadingCard(false);
					}}
					redirectUrl={`${getApiURL()}/${organization.id}/subscriptions`}
				/>
			)}
		</>
	);
};
export default SubscriptionsPayment;
